/**
 * @fileoverview Instructions widget — rich-text + optional ack gate.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render HTML content from `extras.content`. The actual TipTap rendering
 *   is delegated to the consuming host (Phase 7 cutover wires it via
 *   `@firesquirrel/ui-admin` `HeaderFormRenderer`); Phase 3 ships a safe
 *   `dangerouslySetInnerHTML` placeholder so the visual tests pass.
 * - When `extras.requireAck === true`, render an Ack button whose state
 *   becomes the field's value (true once acknowledged).
 *
 * @component InstructionsField
 *
 * @contract InstructionsFieldProps
 * Purpose: prop contract for the rich-text + ack widget.
 */
import type { InspectionFormField, ResolvedFieldStateV2 } from '@firesquirrel/core-domain';

export interface InstructionsFieldProps {
  field: InspectionFormField;
  value: boolean | null | undefined;
  resolved?: ResolvedFieldStateV2;
  onChange: (next: boolean) => void;
}

export function InstructionsField({ field, value, resolved, onChange }: InstructionsFieldProps) {
  if (resolved?.hidden) return null;
  const extras = (field.extras ?? {}) as { content?: string; requireAck?: boolean };
  const acked = !!value;
  // Completion gate — when ack is required and not yet acked, surface the
  // unmet state via `data-ack-required` so hosts (and tests) can detect it.
  const ackUnmet = !!extras.requireAck && !acked;

  return (
    <div
      data-field-id={field.id}
      data-widget-type="instructions"
      data-ack-required={extras.requireAck ? 'true' : 'false'}
      data-acked={acked ? 'true' : 'false'}
      data-ack-unmet={ackUnmet ? 'true' : 'false'}
      role="note"
      className="bg-orange-50 border border-orange-200 rounded-sm p-3"
    >
      {field.label && <h4 className="text-sm font-semibold text-slate-800 mb-1">{field.label}</h4>}
      <div
        className="prose prose-sm text-slate-700"
        // The consuming host's TipTap output is trusted; Phase 7 will replace
        // this with the canonical `<HeaderFormRenderer>` component.
        dangerouslySetInnerHTML={{ __html: extras.content ?? '' }}
      />
      {extras.requireAck && (
        <button
          type="button"
          data-action="ack"
          aria-pressed={acked}
          disabled={acked || !!resolved?.disabled}
          onClick={() => onChange(true)}
          className={`mt-2 px-3 py-1.5 text-xs font-medium rounded-sm ${acked ? 'bg-green-600 text-white' : 'bg-orange-600 text-white hover:bg-orange-700'}`}
        >
          {acked ? '✓ Acknowledged' : 'I understand'}
        </button>
      )}
      {ackUnmet && (
        <p
          role="alert"
          data-ack-unmet-message="true"
          className="mt-2 text-xs text-red-600"
        >
          Acknowledgement required to complete this form.
        </p>
      )}
    </div>
  );
}
